import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { getTokenUsageSummary, getTokenUsageDetails, getPerformanceSummary, getBookingAnalytics } from "../../Services/chatbot";
import KpiCards from "./KpiCards";
import UsageTrendChart from "./UsageTrendChart";
import DonutChart from "./DonutChart";
import UsageTable from "./UsageTable";
import AnswerRateChart from "./AnswerRateChart";
import TopFailingQuestions from "./TopFailingQuestions";
import ConfidenceHistogram from "./ConfidenceHistogram";
import ChatbotTips from "./ChatbotTips";
import BookingKpiCards from "./BookingKpiCards";
import BookingFunnelChart from "./BookingFunnelChart";
import DailyBookingsChart from "./DailyBookingsChart";
import RoomPopularityTable from "./RoomPopularityTable";
import AbandonmentChart from "./AbandonmentChart";
import PeakHoursChart from "./PeakHoursChart";
import { BarChart3, CalendarCheck, Gauge, Loader2, SlidersHorizontal, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, SectionCard } from "@/components/admin/cards";
import { cn } from "@/lib/utils";

const formatDateParam = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dy}`;
};

const getPresetRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = new Date(today);

  switch (preset) {
    case "today": {
      return { from: new Date(today), to };
    }
    case "7d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from, to };
    }
    case "30d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from, to };
    }
    case "month": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from, to };
    }
    default: {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from, to };
    }
  }
};

const TokenAnalytics = ({ propertyId }: any) => {
  const [subTab, setSubTab] = useState("usage");
  const [preset, setPreset] = useState("7d");
  const [dateRange, setDateRange] = useState(() => getPresetRange("7d"));
  const [loading, setLoading] = useState(true);

  // Usage tab state
  const [usageData, setUsageData] = useState<any>({ daily: [], totals: {} });
  const [previousTotals, setPreviousTotals] = useState<any>({});
  const [detailsData, setDetailsData] = useState<any>({ model_breakdown: {}, purpose_breakdown: {} });

  // Performance tab state
  const [perfData, setPerfData] = useState<any>({
    daily_answer_rate: [],
    source_distribution: [],
    top_failing_questions: [],
    confidence_buckets: [],
  });

  // Booking tab state
  const [bookingData, setBookingData] = useState<any>({
    funnel: {},
    daily_bookings: [],
    room_popularity: [],
    revenue_summary: {},
    abandonment_analysis: [],
    peak_booking_hours: [],
    avg_booking_duration_minutes: 0,
    previous_period: {},
  });

  const fromStr = useMemo(() => formatDateParam(dateRange.from), [dateRange.from]);
  const toStr = useMemo(() => formatDateParam(dateRange.to), [dateRange.to]);

  const loadUsageData = useCallback(async () => {
    setLoading(true);
    try {
      const summaryRes = await getTokenUsageSummary(fromStr, toStr);
      const summaryData = summaryRes?.data || {};
      setUsageData({
        daily: summaryData.daily || [],
        totals: summaryData.totals || {},
      });
      setPreviousTotals(summaryData.previous_totals || {});

      // Also load details for model/purpose breakdowns
      const detailsRes = await getTokenUsageDetails(fromStr);
      const details = detailsRes?.data || {};
      setDetailsData({
        model_breakdown: details.model_breakdown || {},
        purpose_breakdown: details.purpose_breakdown || {},
      });
    } catch (err) {
      const msg = err?.message || "Failed to load usage data";
      toast.error(msg);
      console.error("Usage data error:", err);
    } finally {
      setLoading(false);
    }
  }, [fromStr, toStr]);

  const loadPerfData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPerformanceSummary(fromStr, toStr);
      const data = res?.data || {};
      setPerfData({
        daily_answer_rate: data.daily_answer_rate || [],
        source_distribution: data.source_distribution || [],
        top_failing_questions: data.top_failing_questions || [],
        confidence_buckets: data.confidence_buckets || [],
      });
    } catch (err) {
      const msg = err?.message || "Failed to load performance data";
      toast.error(msg);
      console.error("Performance data error:", err);
    } finally {
      setLoading(false);
    }
  }, [fromStr, toStr]);

  const loadBookingData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBookingAnalytics(fromStr, toStr);
      const data = res?.data || {};
      setBookingData({
        funnel: data.funnel || {},
        daily_bookings: data.daily_bookings || [],
        room_popularity: data.room_popularity || [],
        revenue_summary: data.revenue_summary || {},
        abandonment_analysis: data.abandonment_analysis || [],
        peak_booking_hours: data.peak_booking_hours || [],
        avg_booking_duration_minutes: data.avg_booking_duration_minutes || 0,
        previous_period: data.previous_period || {},
      });
    } catch (err) {
      const msg = err?.message || "Failed to load booking analytics";
      toast.error(msg);
      console.error("Booking analytics error:", err);
    } finally {
      setLoading(false);
    }
  }, [fromStr, toStr]);

  useEffect(() => {
    if (subTab === "usage") {
      loadUsageData();
    } else if (subTab === "performance") {
      loadPerfData();
    } else if (subTab === "booking") {
      loadBookingData();
    }
  }, [subTab, loadUsageData, loadPerfData, loadBookingData]);

  const handlePreset = (p) => {
    setLoading(true);
    setPreset(p);
    setDateRange(getPresetRange(p));
  };

  const handleDateChange = (field, value) => {
    setLoading(true);
    setPreset("");
    setDateRange((prev) => ({
      ...prev,
      [field]: new Date(value + "T00:00:00"),
    }));
  };

  // Transform breakdowns for donut charts
  const modelDonutData = useMemo(() => {
    const mb = detailsData.model_breakdown;
    return Object.keys(mb).map((key) => ({
      label: key,
      value: mb[key]?.tokens || mb[key]?.total_tokens || 0,
    }));
  }, [detailsData.model_breakdown]);

  const purposeDonutData = useMemo(() => {
    const pb = detailsData.purpose_breakdown;
    return Object.keys(pb).map((key) => ({
      label: key,
      value: pb[key]?.calls || pb[key]?.total_calls || 0,
    }));
  }, [detailsData.purpose_breakdown]);

  const SOURCE_LABEL_MAP = {
    kb: "From Your FAQs",
    knowledge_base: "From Your FAQs",
    faq: "From Your FAQs",
    llm: "AI Generated",
    ai: "AI Generated",
    gpt: "AI Generated",
    failed: "Couldn't Answer",
    unanswered: "Couldn't Answer",
    no_answer: "Couldn't Answer",
  };

  const sourceDonutData = useMemo(() => {
    return (perfData.source_distribution || []).map((s) => {
      const rawLabel = (s.source || s.label || "Unknown").toLowerCase();
      return {
        label: SOURCE_LABEL_MAP[rawLabel] || s.source || s.label || "Other",
        value: s.count || s.value || 0,
      };
    });
  }, [perfData.source_distribution]);

  const PRESETS = [
    { key: "today", label: "Today" },
    { key: "7d", label: "Last 7 Days" },
    { key: "30d", label: "Last 30 Days" },
    { key: "month", label: "This Month" },
  ];
  const SUB_TABS = [
    { key: "usage", label: "Usage", icon: <Zap aria-hidden="true" /> },
    { key: "performance", label: "Performance", icon: <Gauge aria-hidden="true" /> },
    { key: "booking", label: "Booking Analytics", icon: <CalendarCheck aria-hidden="true" /> },
  ];

  const handleSubTab = (key) => {
    setLoading(true);
    setSubTab(key);
    if (key === "usage" && preset === "today") {
      setPreset("7d");
      setDateRange(getPresetRange("7d"));
    }
  };

  const segmentClass = (active) => cn(
    "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground [&_svg]:size-4",
    active && "active bg-background text-foreground shadow-sm"
  );

  return (
    <div className="analytics-dashboard flex flex-col gap-4">
      <PageHeader id="analyticsHeading" icon={<BarChart3 />} title="Analytics"
        description="How guests use your chatbot, how well it answers and the bookings it brings." />

      <div data-evo-island className="flex flex-col gap-4">
        <SectionCard icon={<SlidersHorizontal />} title="What to show" description="Pick a report and a date range.">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="tablist" aria-label="Analytics report" className="analytics-sub-tabs flex flex-wrap gap-1 rounded-lg bg-muted p-1 max-sm:w-full">
              {SUB_TABS.map((t) => (
                <button key={t.key} type="button" role="tab" aria-selected={subTab === t.key}
                  className={cn("sub-tab-btn max-sm:flex-1", segmentClass(subTab === t.key))} onClick={() => handleSubTab(t.key)}>
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
            <div className="analytics-date-row flex flex-wrap items-center gap-2">
              <div className="analytics-date-presets flex flex-wrap gap-1 rounded-lg bg-muted p-1" role="group" aria-label="Date presets">
                {PRESETS.filter((p) => !(subTab === "usage" && p.key === "today")).map((p) => (
                  <button type="button" key={p.key} aria-pressed={preset === p.key}
                    className={cn("preset-btn", segmentClass(preset === p.key))} onClick={() => handlePreset(p.key)}>
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="analytics-date-inputs flex items-center gap-2 text-sm">
                <Input type="date" aria-label="From date" className="h-9 w-auto" value={fromStr} onChange={(e) => handleDateChange("from", e.target.value)} />
                <span className="text-muted-foreground">to</span>
                <Input type="date" aria-label="To date" className="h-9 w-auto" value={toStr} onChange={(e) => handleDateChange("to", e.target.value)} />
              </div>
            </div>
          </div>
        </SectionCard>

        {loading && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-16 text-sm text-muted-foreground" role="status">
            <Loader2 className="size-5 animate-spin text-primary" aria-hidden="true" />
            Loading analytics...
          </div>
        )}

        {subTab === "usage" && !loading && (
          <>
            <KpiCards totals={usageData.totals} previousTotals={previousTotals} />
            <UsageTrendChart daily={usageData.daily} />
            {/* Donut charts commented out — will revisit later
            <div className="donut-charts-row">
              <DonutChart title="Tokens by AI Model" data={modelDonutData} centerLabel="tokens" />
              <DonutChart title="Calls by Purpose" data={purposeDonutData} centerLabel="calls" />
            </div>
            */}
            <UsageTable daily={usageData.daily} />
          </>
        )}

        {subTab === "performance" && !loading && (
          <>
            <AnswerRateChart daily={perfData.daily_answer_rate} />
            <div className="donut-charts-row grid gap-4 lg:grid-cols-2">
              <DonutChart title="Where Answers Come From" data={sourceDonutData} centerLabel="answers" />
              <ConfidenceHistogram buckets={perfData.confidence_buckets} />
            </div>
            <TopFailingQuestions questions={perfData.top_failing_questions} />
            <ChatbotTips
              dailyAnswerRate={perfData.daily_answer_rate}
              failingQuestions={perfData.top_failing_questions}
              confidenceBuckets={perfData.confidence_buckets}
            />
          </>
        )}

        {subTab === "booking" && !loading && (
          <>
            <BookingKpiCards
              funnel={bookingData.funnel}
              previousPeriod={bookingData.previous_period}
              avgBookingDuration={bookingData.avg_booking_duration_minutes}
            />
            <BookingFunnelChart funnel={bookingData.funnel} />
            <DailyBookingsChart daily={bookingData.daily_bookings} />
            <div className="donut-charts-row grid gap-4 lg:grid-cols-2">
              <RoomPopularityTable rooms={bookingData.room_popularity} noAvailabilityCount={bookingData.funnel?.no_availability || 0} />
              <PeakHoursChart hours={bookingData.peak_booking_hours} />
            </div>
            <AbandonmentChart analysis={bookingData.abandonment_analysis} funnel={bookingData.funnel} />
          </>
        )}
      </div>
    </div>
  );
};

export default TokenAnalytics;
